<template>
    <v-card 
        outlined 
        class="ambience-card" 
        :class="{ 'ambience-card--clickable': clickable }"
        @click="onClick"
    >
        <v-card-text class="pb-2">
            <div class="d-flex justify-space-between align-start mb-2">
                <div>
                    <div class="text-subtitle-1 font-weight-medium">
                        {{ ambience.name }}
                    </div>
                    <div class="text-caption grey--text">
                        {{ locationText }}
                    </div>
                </div>
                
                <v-btn 
                    v-if="showEditButton" 
                    icon 
                    small 
                    @click.stop="$emit('edit', ambience)"
                >
                    <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
            </div>

            <v-divider class="my-2" />

            <div class="d-flex align-center mb-2">
                <v-icon small class="mr-1" color="grey">mdi-account-group</v-icon>
                <span class="text-body-2">
                    {{ activeCapacity }} lugares
                </span>
                <v-chip 
                    x-small 
                    :color="capacityModeColor" 
                    class="ml-2"
                    outlined
                >
                    {{ capacityModeLabel }}
                </v-chip>
            </div>

            <div v-if="ambience.type" class="mb-2">
                <v-chip 
                    x-small 
                    color="primary" 
                    outlined
                >
                    {{ ambience.type.name || ambience.type }}
                </v-chip>
            </div>

            <sys-ambience-resource-tags
                v-if="hasResources"
                :resources="ambience.resources"
                :max-display="4"
                x-small
                class="mt-2"
            />

            <div v-if="showOccupation" class="mt-3">
                <div class="d-flex justify-space-between align-center mb-1">
                    <span class="text-caption grey--text">Ocupação</span>
                    <span class="text-caption font-weight-medium">{{ occupationPercent }}%</span>
                </div>
                <v-progress-linear
                    :value="occupationPercent"
                    :color="occupationColor"
                    height="6"
                    rounded
                />
            </div>
        </v-card-text>

        <v-card-actions v-if="showActions" class="pt-0">
            <v-spacer />
            <v-btn 
                text 
                small 
                color="primary" 
                @click.stop="$emit('view', ambience)"
            >
                Ver detalhes
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import SysAmbienceResourceTags from './ResourceTags.vue'

export default {
    name: 'SysAmbienceCard',
    
    components: {
        SysAmbienceResourceTags
    },
    
    props: {
        ambience: {
            type: Object,
            required: true
        },
        showEditButton: {
            type: Boolean,
            default: true
        },
        showActions: {
            type: Boolean,
            default: false
        },
        showOccupation: {
            type: Boolean,
            default: false
        },
        currentStudents: {
            type: Number,
            default: 0
        },
        clickable: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        locationText() {
            const parts = []
            
            if (this.ambience.block?.name) {
                parts.push(this.ambience.block.name)
            }
            
            if (this.ambience.floor?.name) {
                parts.push(this.ambience.floor.name)
            }
            
            if (this.ambience.campus?.name) {
                parts.push(this.ambience.campus.name)
            }
            
            return parts.join(' - ') || 'Localização não definida'
        },
        
        activeCapacity() {
            const mode = this.ambience.capacity_mode || 'CONV'
            
            switch (mode) {
                case 'RED':
                    return this.ambience.reduced_capacity || 0
                case 'AMP':
                    return this.ambience.extended_capacity || 0
                default:
                    return this.ambience.capacity || 0
            }
        },
        
        capacityModeLabel() {
            const mode = this.ambience.capacity_mode || 'CONV'
            
            switch (mode) {
                case 'RED':
                    return 'Reduzida'
                case 'AMP':
                    return 'Ampliada'
                default:
                    return 'Convencional'
            }
        },
        
        capacityModeColor() {
            const mode = this.ambience.capacity_mode || 'CONV'
            
            switch (mode) {
                case 'RED':
                    return 'warning'
                case 'AMP':
                    return 'success'
                default:
                    return 'primary'
            }
        },
        
        hasResources() {
            return this.ambience.resources && this.ambience.resources.length > 0
        },
        
        occupationPercent() {
            if (!this.activeCapacity || this.activeCapacity === 0) {
                return 0
            }
            return Math.round((this.currentStudents / this.activeCapacity) * 100)
        },
        
        occupationColor() {
            const percent = this.occupationPercent
            
            if (percent <= 50) return 'success'
            if (percent <= 75) return 'warning'
            if (percent <= 100) return 'orange'
            return 'error'
        }
    },

    methods: {
        onClick() {
            if (this.clickable) {
                this.$emit('click', this.ambience)
            }
        }
    }
}
</script>

<style scoped>
.ambience-card {
    transition: all 0.2s ease;
}

.ambience-card--clickable {
    cursor: pointer;
}

.ambience-card--clickable:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
}
</style>
